import { useState, useEffect, useRef } from 'react'
import { Link, router } from '@inertiajs/react'
import { AdminLayout } from '@/layouts/AdminLayout'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Search, X, FileText, Plus, MoreVertical, Eye, Pencil, Trash2, Send, XCircle } from 'lucide-react'

interface Post {
  id: number
  title: string
  slug: string
  excerpt: string | null
  author_name: string | null
  cover_image_url: string | null
  published_at: string | null
  reading_time_min: number | null
  status: string
  tags: Array<{ id: number; name: string; slug: string }>
  created_at: string
}

interface Props {
  posts: Post[]
  tags: Array<{ id: number; name: string; slug: string }>
  meta: { current_page: number; total_pages: number; total_count: number }
  filters: { status?: string; tag?: string; search?: string }
  stats: { total: number; published: number; drafts: number }
}

export default function Index({ posts, tags, meta, filters, stats }: Props) {
  const [search, setSearch] = useState(filters.search || '')
  const [statusFilter, setStatusFilter] = useState(filters.status || '')
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null)
  const didMount = useRef(false)

  useEffect(() => {
    if (!didMount.current) { didMount.current = true; return }
    const id = setTimeout(() => {
      const params: Record<string, string> = { page: '1' }
      if (search) params.search = search
      if (statusFilter) params.status = statusFilter
      if (filters.tag) params.tag = filters.tag
      router.get('/admin/posts', params, { preserveState: true, replace: true })
    }, 400)
    return () => clearTimeout(id)
  }, [search, statusFilter])

  const handleStatusFilter = (status: string) => {
    setStatusFilter(statusFilter === status ? '' : status)
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-2xl font-bold text-foreground">Blog Posts</h1>
          <Button asChild>
            <Link href="/admin/posts/new">
              <Plus className="mr-1 h-4 w-4" /> Nuevo Post
            </Link>
          </Button>
        </div>

        {/* Stats */}
        <div className="grid gap-4 sm:grid-cols-3">
          <Card className="cursor-pointer" onClick={() => handleStatusFilter('')}>
            <CardContent className="p-4">
              <p className="text-2xl font-bold">{stats.total}</p>
              <p className="text-sm text-muted-foreground">Total</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer" onClick={() => handleStatusFilter('published')}>
            <CardContent className="p-4">
              <p className="text-2xl font-bold text-green-500">{stats.published}</p>
              <p className="text-sm text-muted-foreground">Publicados</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer" onClick={() => handleStatusFilter('draft')}>
            <CardContent className="p-4">
              <p className="text-2xl font-bold text-muted-foreground">{stats.drafts}</p>
              <p className="text-sm text-muted-foreground">Borradores</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Buscar por título..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 pr-10"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          {statusFilter && (
            <Button variant="ghost" size="sm" onClick={() => setStatusFilter('')}>
              <X className="mr-1 h-4 w-4" />
              {statusFilter === 'published' ? 'Publicados' : 'Borradores'}
            </Button>
          )}
        </div>

        {/* Tag filter */}
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {tags.map((tag) => (
              <Badge
                key={tag.id}
                variant={filters.tag === tag.slug ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => {
                  const params: Record<string, string> = { page: '1' }
                  if (search) params.search = search
                  if (statusFilter) params.status = statusFilter
                  if (filters.tag !== tag.slug) params.tag = tag.slug
                  router.get('/admin/posts', params, { preserveState: true, replace: true })
                }}
              >
                {tag.name}
                {filters.tag === tag.slug && <X className="ml-1 h-3 w-3" />}
              </Badge>
            ))}
          </div>
        )}

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Título</TableHead>
                  <TableHead>Tags</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Autor</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {posts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-12">
                      <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                      <p className="text-muted-foreground">No hay posts</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  posts.map((post) => (
                    <TableRow key={post.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          {post.cover_image_url ? (
                            <img src={post.cover_image_url} alt="" className="w-10 h-10 rounded object-cover" />
                          ) : (
                            <div className="w-10 h-10 rounded bg-muted flex items-center justify-center">
                              <FileText className="h-5 w-5 text-muted-foreground" />
                            </div>
                          )}
                          <div>
                            <p className="font-medium">{post.title}</p>
                            <p className="text-xs text-muted-foreground">/{post.slug}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {post.tags.map((t) => (
                            <Badge key={t.id} variant="secondary" className="text-xs">{t.name}</Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={post.status === 'published' ? 'success' : 'secondary'}>
                          {post.status === 'published' ? 'Publicado' : 'Borrador'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{post.author_name || '—'}</TableCell>
                      <TableCell className="text-muted-foreground text-xs">
                        {post.published_at
                          ? new Date(post.published_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })
                          : 'Sin publicar'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" asChild>
                            <Link href={`/admin/posts/${post.id}/edit`}>
                              <Pencil className="h-4 w-4" />
                            </Link>
                          </Button>
                          {post.status === 'published' ? (
                            <Button variant="ghost" size="sm" onClick={() => router.post(`/admin/posts/${post.id}/unpublish`)}>
                              <XCircle className="h-4 w-4" />
                            </Button>
                          ) : (
                            <Button variant="ghost" size="sm" onClick={() => router.post(`/admin/posts/${post.id}/publish`)}>
                              <Send className="h-4 w-4" />
                            </Button>
                          )}
                          {post.status === 'published' && (
                            <Button variant="ghost" size="sm" asChild>
                              <a href={`/blog/${post.slug}`} target="_blank">
                                <Eye className="h-4 w-4" />
                              </a>
                            </Button>
                          )}
                          {deleteConfirm === post.id ? (
                            <div className="flex items-center gap-1">
                              <Button variant="destructive" size="sm" onClick={() => { router.delete(`/admin/posts/${post.id}`); setDeleteConfirm(null) }}>
                                Sí
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => setDeleteConfirm(null)}>
                                No
                              </Button>
                            </div>
                          ) : (
                            <Button variant="ghost" size="sm" onClick={() => setDeleteConfirm(post.id)}>
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Pagination */}
        {meta.total_pages > 1 && (
          <div className="flex items-center justify-center gap-2">
            {meta.current_page > 1 && (
              <Button variant="outline" size="sm" asChild>
                <Link href={`/posts?page=${meta.current_page - 1}`}>Anterior</Link>
              </Button>
            )}
            <span className="text-sm text-muted-foreground">
              Página {meta.current_page} de {meta.total_pages}
            </span>
            {meta.current_page < meta.total_pages && (
              <Button variant="outline" size="sm" asChild>
                <Link href={`/posts?page=${meta.current_page + 1}`}>Siguiente</Link>
              </Button>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  )
}
